/**
 * 지시-실행 트래커 데이터.
 *
 * 원장이 지시를 내리면 강사 앱으로 넘어가고, 강사가 실행 후 결과를 보고하면
 * 다시 원장 화면에 돌아온다. 이 왕복이 닫혀야 "지시했는데 어떻게 됐지?"가 사라진다.
 *
 * ⚠️ 임시 데이터입니다. 서버 API 연동 시 제거합니다.
 */

export type OrderStatus =
    /** 전달됨 — 강사가 아직 행동하지 않음 */
    | 'SENT'
    /** 강사가 확인함 */
    | 'SEEN'
    /** 완료 + 결과 보고 있음 */
    | 'DONE'
    /** 24시간 초과 미실행 */
    | 'OVERDUE'
    /** 원장이 확인 완료 — 목록에서 사라짐 */
    | 'ARCHIVED';

export type Order = {
    orderId: string;
    /** 대상 회원 */
    memberName: string;
    /** 수행할 강사 */
    coachName: string;
    /** 무엇을 지시했나 */
    task: string;
    /**
     * 원장이 **강사에게** 남긴 말. 회원에게 보낼 글이 아니다.
     * 회원에게 건넬 문구는 memberDraft 다 — 받는 사람이 다르므로 칸도 다르다.
     */
    ownerNote: string;
    /** 회원에게 건넬 문구. 없으면 null (빈 문자열로 두지 않는다) */
    memberDraft: string | null;
    status: OrderStatus;
    sentAt: string;
    seenAt?: string;
    doneAt?: string;
    /** 강사가 남긴 결과 보고 */
    coachReport?: string;
    /** 누가 완료 처리했나. 강사 보고와 원장 직접 처리는 신뢰도가 다르다 */
    doneBy?: 'COACH' | 'OWNER';
    /** 며칠 전에 보낸 지시인가. 0 = 오늘. 오늘 낸 것과 밀린 것을 갈라 보여주려고 쓴다 */
    sentDayOffset: number;
    /**
     * 강사가 완료 보고했는데 원장이 다시 연 건.
     * 보고 내용은 지우지 않고 남긴다 — 말이 엇갈렸다는 사실 자체가 기록이다.
     */
    reopened?: boolean;
    /** 이 지시로 지키려는 금액 */
    valueAmount: number;
    valueKind: 'DEFEND' | 'GAIN';
};

export const STATUS_META: Record<
    Exclude<OrderStatus, 'ARCHIVED'>,
    { label: string; color: string; bg: string; dot: string }
> = {
    SENT: { label: '전달 완료 · 대기중', color: '#B45309', bg: '#FEF3C7', dot: '#F0A020' },
    SEEN: { label: '강사 확인함', color: '#2B529D', bg: '#EAF1FD', dot: '#3D73DD' },
    DONE: { label: '완료 · 결과 보고', color: '#00795F', bg: '#E3F1EC', dot: '#00A793' },
    OVERDUE: { label: '미실행 · 기한 초과', color: '#C22D38', bg: '#FFE8E9', dot: '#FF3E4A' },
};

export const mockOrders: Order[] = [
    {
        orderId: 'o1',
        memberName: '한수경',
        sentDayOffset: 1,
        coachName: '김한솔',
        task: '수강권 소진 지연 — 연락 요청',
        ownerNote: '이번 주에 한번 연락해 보세요. 시간 안 맞으면 옮겨드려도 됩니다.',
        memberDraft:
            '수경님, 김한솔입니다. 요즘 통 못 뵈어서 안부 여쭤봐요.\n편하실 때 다음 수업 시간 잡아드릴까요?',
        status: 'DONE',
        sentAt: '어제 09:14',
        seenAt: '어제 09:52',
        doneAt: '오늘 08:12',
        coachReport: '금일 통화 완료했습니다. 이번 주 금요일에 재등록하기로 하셨어요!',
        doneBy: 'COACH',
        valueAmount: 900000,
        valueKind: 'DEFEND',
    },
    {
        orderId: 'o2',
        memberName: '장윤문',
        sentDayOffset: 1,
        coachName: '성현',
        task: '재등록 상담 지시',
        ownerNote: '잔여 3회 남았습니다. 다음 수업 때 자연스럽게 꺼내주세요.',
        memberDraft:
            '윤문님, 성현입니다. 수강권이 3회 남으셔서 미리 말씀드려요.\n다음 수업 때 일정 같이 정리해 봐요.',
        status: 'SEEN',
        sentAt: '어제 09:14',
        seenAt: '어제 18:40',
        valueAmount: 1200000,
        valueKind: 'GAIN',
    },
    {
        orderId: 'o3',
        memberName: '강도경',
        sentDayOffset: 0,
        coachName: '조은오',
        task: '2주 미출석 — 안부 연락',
        ownerNote: '컨디션 문제인지 확인 부탁드려요.',
        // 문구 없이 나간 지시 — 화면이 '문구 없음'을 제대로 말하는지 여기서 확인한다
        memberDraft: null,
        status: 'SENT',
        sentAt: '오늘 09:02',
        valueAmount: 600000,
        valueKind: 'DEFEND',
    },
    {
        orderId: 'o4',
        memberName: '최지은',
        sentDayOffset: 2,
        coachName: '성현',
        task: '노쇼 2회 — 원인 확인',
        ownerNote: '시간대가 안 맞는 건지 확인해 주세요.',
        memberDraft:
            '지은님, 성현입니다. 최근 수업을 두 번 못 뵈어서 연락드려요.\n시간대가 안 맞으시면 옮겨드릴게요.',
        status: 'OVERDUE',
        sentAt: '그저께 09:07',
        valueAmount: 750000,
        valueKind: 'DEFEND',
    },
];

/** 강사 앱에서 보는 오늘의 할 일 — 성현 강사 기준 */
export const mockCoachTasks: Order[] = mockOrders.filter(
    (o) => o.coachName === '성현' && o.status !== 'ARCHIVED'
);
